import React, { useState, useEffect } from 'react';
import { WarehouseNode, CreateNodeRequest, UpdateNodeRequest } from '../../types';
import { NodeService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import NodeList from './NodeList';
import NodeForm from './NodeForm';
import './NodeManagement.css';

const NodeManagement: React.FC = () => {
  const { state: authState } = useAuth();
  const [nodes, setNodes] = useState<WarehouseNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingNode, setEditingNode] = useState<WarehouseNode | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Load nodes on component mount and when authentication state changes
  useEffect(() => {
    if (authState.isAuthenticated) {
      loadNodes();
    }
  }, [authState.isAuthenticated]);

  // Make nodes data available globally for AI Assistant context
  useEffect(() => {
    if (!loading && nodes.length >= 0 && authState.isAuthenticated) {
      (window as any).warehouseNodesData = {
        nodes,
        tenant_id: authState.user?.tenant_id,
        user_id: authState.user?.user_id
      };
    }
  }, [nodes, loading, authState.isAuthenticated, authState.user]);

  const loadNodes = async () => {
    if (!authState.isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const fetchedNodes = await NodeService.getNodes();
      
      // Filter nodes by tenant_id as an additional security measure
      const tenantNodes = fetchedNodes.filter(node => 
        node.tenant_id === authState.user?.tenant_id
      );
      
      setNodes(tenantNodes);
    } catch (err) {
      if (err instanceof Error && err.message.includes('401')) {
        setError('Session expired. Please log in again.');
      } else if (err instanceof Error && err.message.includes('403')) {
        setError('Access denied. You do not have permission to view nodes.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load warehouse nodes');
      }
      console.error('Error loading nodes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNode = async (nodeData: CreateNodeRequest) => {
    if (!authState.isAuthenticated) {
      throw new Error('Authentication required');
    }

    try {
      setFormLoading(true);
      setError(null);
      const newNode = await NodeService.createNode(nodeData);
      
      // Verify the new node belongs to the current tenant
      if (newNode.tenant_id === authState.user?.tenant_id) {
        setNodes(prev => [...prev, newNode]);
        setShowForm(false);
        setEditingNode(null);
      } else {
        throw new Error('Node creation failed: tenant mismatch');
      }
    } catch (err) {
      if (err instanceof Error && err.message.includes('401')) {
        setError('Session expired. Please log in again.');
      } else if (err instanceof Error && err.message.includes('403')) {
        setError('Access denied. You do not have permission to create nodes.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create node');
      }
      throw err; // Re-throw to let form handle it
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateNode = async (nodeData: UpdateNodeRequest) => {
    if (!editingNode || !authState.isAuthenticated) return;

    // Verify node belongs to current tenant
    if (editingNode.tenant_id !== authState.user?.tenant_id) {
      setError('Access denied. You cannot modify nodes from other tenants.');
      return;
    }

    try {
      setFormLoading(true);
      setError(null);
      const updatedNode = await NodeService.updateNode(editingNode.node_id, nodeData);
      
      // Verify the updated node still belongs to the current tenant
      if (updatedNode.tenant_id === authState.user?.tenant_id) {
        setNodes(prev => prev.map(node => 
          node.node_id === editingNode.node_id ? updatedNode : node
        ));
        setShowForm(false);
        setEditingNode(null);
      } else {
        throw new Error('Node update failed: tenant mismatch');
      }
    } catch (err) {
      if (err instanceof Error && err.message.includes('401')) {
        setError('Session expired. Please log in again.');
      } else if (err instanceof Error && err.message.includes('403')) {
        setError('Access denied. You do not have permission to update this node.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to update node');
      }
      throw err; // Re-throw to let form handle it
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteNode = async (nodeId: string) => {
    if (!authState.isAuthenticated) {
      setError('Authentication required');
      return;
    }

    const nodeToDelete = nodes.find(n => n.node_id === nodeId);
    if (!nodeToDelete) return;

    // Verify node belongs to current tenant
    if (nodeToDelete.tenant_id !== authState.user?.tenant_id) {
      setError('Access denied. You cannot delete nodes from other tenants.');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${nodeToDelete.name}"? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      setError(null);
      await NodeService.deleteNode(nodeId);
      setNodes(prev => prev.filter(node => node.node_id !== nodeId));
    } catch (err) {
      if (err instanceof Error && err.message.includes('401')) {
        setError('Session expired. Please log in again.');
      } else if (err instanceof Error && err.message.includes('403')) {
        setError('Access denied. You do not have permission to delete this node.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to delete node');
      }
      console.error('Error deleting node:', err);
    }
  };

  const handleEditNode = (node: WarehouseNode) => {
    // Verify node belongs to current tenant
    if (node.tenant_id !== authState.user?.tenant_id) {
      setError('Access denied. You cannot edit nodes from other tenants.');
      return;
    }
    
    setEditingNode(node);
    setShowForm(true);
  };

  const handleAddNewNode = () => {
    if (!authState.isAuthenticated) {
      setError('Authentication required');
      return;
    }
    
    setEditingNode(null);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingNode(null);
    setError(null);
  };

  const handleFormSubmit = async (nodeData: CreateNodeRequest | UpdateNodeRequest) => {
    if (editingNode) {
      await handleUpdateNode(nodeData as UpdateNodeRequest);
    } else {
      await handleCreateNode(nodeData as CreateNodeRequest);
    }
  };

  // Show authentication required message if not authenticated
  if (!authState.isAuthenticated) {
    return (
      <div className="node-management">
        <div className="auth-required">
          <h2>Authentication Required</h2>
          <p>Please log in to manage warehouse nodes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="node-management">
      <div className="node-management-header">
        <h2>Warehouse Node Management</h2>
        <p>Manage warehouse locations in your optimization network</p>
        {authState.user?.tenant?.name && (
          <div className="tenant-context">
            <span className="tenant-label">Organization:</span>
            <span className="tenant-name">{authState.user.tenant.name}</span>
          </div>
        )}
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button 
            className="error-close"
            onClick={() => setError(null)}
            title="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {!showForm && (
        <div className="node-management-actions">
          <button 
            className="btn btn-primary"
            onClick={handleAddNewNode}
            disabled={loading}
          >
            <span className="btn-icon">+</span>
            Add New Node
          </button>
          <button 
            className="btn btn-secondary"
            onClick={loadNodes}
            disabled={loading}
          >
            <span className="btn-icon">🔄</span>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      )}

      {showForm ? (
        <NodeForm
          node={editingNode || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleCancelForm}
          loading={formLoading}
        />
      ) : (
        <NodeList
          nodes={nodes}
          onEditNode={handleEditNode}
          onDeleteNode={handleDeleteNode}
          loading={loading}
        />
      )}

      {!loading && !showForm && nodes && nodes.length > 0 && (
        <div className="node-management-summary">
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-value">{nodes.length}</span>
              <span className="stat-label">Total Nodes</span>
            </div>
            <div className="stat">
              <span className="stat-value">{nodes.filter(n => n.active).length}</span>
              <span className="stat-label">Active Nodes</span>
            </div>
            <div className="stat">
              <span className="stat-value">
                {nodes.filter(n => n.capacity).length}
              </span>
              <span className="stat-label">With Capacity Limits</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NodeManagement;